import { useState } from 'react';
import { Check, Hash, Search, Users as UsersIcon } from 'lucide-react';
import { useCreateChannel, useCreateGroup, usePeopleSearch, useStartDm, useWorkspaces } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { Avatar, PageLoader, Segmented } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';

type Mode = 'dm' | 'group' | 'channel';

/** Start a direct message (search by nickname or name), create a group, or open a workspace channel. */
export function NewConversationSheet({ open, onClose, onOpened }: { open: boolean; onClose: () => void; onOpened: (channelId: string) => void }) {
  const [mode, setMode] = useState<Mode>('dm');
  return (
    <Sheet open={open} onClose={onClose} variant="center" title="Nuova conversazione" labelledBy="new-chat-title" full>
      <Segmented label="Tipo" value={mode} onChange={setMode} options={[{ value: 'dm', label: 'Diretta' }, { value: 'group', label: 'Gruppo' }, { value: 'channel', label: 'Canale' }]} />
      <div style={{ marginTop: 14 }}>
        {mode === 'dm' && <DmPicker onOpened={onOpened} onClose={onClose} />}
        {mode === 'group' && <GroupForm onOpened={onOpened} onClose={onClose} />}
        {mode === 'channel' && <ChannelForm onOpened={onOpened} onClose={onClose} />}
      </div>
    </Sheet>
  );
}

function PersonSearch({ query, setQuery, selected, onToggle }: { query: string; setQuery: (v: string) => void; selected?: Set<string>; onToggle: (userId: string, displayName: string) => void }) {
  const { orgId } = useOrgCtx();
  const results = usePeopleSearch(orgId, query);
  return (
    <>
      <div className="chat-search">
        <Search size={16} />
        <input className="input" placeholder="Cerca per nickname o nome…" value={query} onChange={(e) => setQuery(e.target.value)} data-autofocus />
      </div>
      {results.isFetching && <PageLoader />}
      {query.trim() && results.data?.length === 0 && !results.isFetching && <p className="hint">Nessuna persona trovata nella tua organizzazione.</p>}
      <div className="list">
        {results.data?.map((p) => (
          <button key={p.userId} type="button" className="list-row" onClick={() => onToggle(p.userId, p.displayName)}>
            <Avatar id={p.userId} name={p.displayName} size={34} />
            <span className="grow">
              <div>{p.displayName}</div>
              {p.nickname && <div className="secondary">@{p.nickname}</div>}
            </span>
            {selected?.has(p.userId) && <Check size={18} color="var(--accent)" />}
          </button>
        ))}
      </div>
    </>
  );
}

function DmPicker({ onOpened, onClose }: { onOpened: (id: string) => void; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const startDm = useStartDm(orgId);
  const toast = useToast();
  const [query, setQuery] = useState('');
  return (
    <PersonSearch
      query={query} setQuery={setQuery}
      onToggle={(userId) => startDm.mutate(userId, {
        onSuccess: (c) => { onOpened(c.id); onClose(); },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Impossibile avviare la conversazione'),
      })}
    />
  );
}

function GroupForm({ onOpened, onClose }: { onOpened: (id: string) => void; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const createGroup = useCreateGroup(orgId);
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [name, setName] = useState('');
  const [members, setMembers] = useState<{ id: string; displayName: string }[]>([]);
  const toggle = (userId: string, displayName: string) =>
    setMembers((m) => (m.some((x) => x.id === userId) ? m.filter((x) => x.id !== userId) : [...m, { id: userId, displayName }]));

  return (
    <>
      <label className="field"><span>Nome del gruppo</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Progetto Lancio" /></label>
      {members.length > 0 && (
        <div className="token-chips" style={{ margin: '8px 0' }}>
          {members.map((m) => (
            <button key={m.id} type="button" className="token" onClick={() => toggle(m.id, m.displayName)}>{m.displayName} ×</button>
          ))}
        </div>
      )}
      <PersonSearch query={query} setQuery={setQuery} selected={new Set(members.map((m) => m.id))} onToggle={toggle} />
      <button
        type="button" className="btn btn-primary" style={{ marginTop: 14, width: '100%' }}
        disabled={!name.trim() || members.length === 0 || createGroup.isPending}
        onClick={() => createGroup.mutate({ name: name.trim(), memberIds: members.map((m) => m.id) }, {
          onSuccess: (c) => { onOpened(c.id); onClose(); },
          onError: (e) => toast(e instanceof ApiError ? e.message : 'Creazione non riuscita'),
        })}
      >
        <UsersIcon size={16} /> Crea gruppo
      </button>
    </>
  );
}

function ChannelForm({ onOpened, onClose }: { onOpened: (id: string) => void; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const workspaces = useWorkspaces(orgId);
  const createChannel = useCreateChannel(orgId);
  const toast = useToast();
  const [workspaceId, setWorkspaceId] = useState('');
  const [name, setName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  if (!workspaceId && workspaces.data?.[0]) setWorkspaceId(workspaces.data[0].id);

  return (
    <>
      <div className="field">
        <span>Spazio</span>
        <select className="input" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}>
          {workspaces.data?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>
      <label className="field"><span>Nome canale</span><input className="input" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder="es. generale" /></label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, margin: '10px 0' }}>
        <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} /> Canale privato
      </label>
      <button
        type="button" className="btn btn-primary" style={{ width: '100%' }}
        disabled={!workspaceId || !name.trim() || createChannel.isPending}
        onClick={() => createChannel.mutate({ workspaceId, name: name.trim(), isPrivate }, {
          onSuccess: (c) => { onOpened(c.id); onClose(); },
          onError: (e) => toast(e instanceof ApiError ? e.message : 'Creazione non riuscita'),
        })}
      >
        <Hash size={16} /> Crea canale
      </button>
    </>
  );
}
